import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { EditorBookingItemTree } from '../editor-booking-item-tree/editor-booking-item-tree.model';
import {
  EditorBookingItemTreeStore
} from '../editor-booking-item-tree/editor-booking-item-tree.store';


export interface PatchingEditorBookingItemTreeFilter {
  name: 'bookingType'|'examType'|'extraRoles';
}


export class PatchingItemTreeMask {
  bookingType: boolean;
  examType: boolean;
  extraRoles: boolean;

  constructor(obj?: any) {
    this.bookingType = obj?.bookingType ?? false;
    this.examType = obj?.examType ?? false;
    this.extraRoles = obj?.extraRoles ?? false;
  }

  static getFromPatchingItemTreeFilter(itemTreeFilter: PatchingEditorBookingItemTreeFilter): PatchingItemTreeMask {
    const bookingType = itemTreeFilter === null || (itemTreeFilter.name === 'bookingType');
    const examType = itemTreeFilter === null || (itemTreeFilter.name === 'examType');
    const extraRoles = itemTreeFilter === null || (itemTreeFilter.name === 'extraRoles');

    return new this({
      bookingType,
      examType,
      extraRoles
    });
  }
}


@Injectable()
export class EditorBookingItemTreeDataEditStore {
  private readonly _replacingItemTree$ = new BehaviorSubject<EditorBookingItemTree>(null);
  private readonly _patchingItemTreeFilter$ = new BehaviorSubject<PatchingEditorBookingItemTreeFilter>(null);
  private readonly _patchingItemTreeMask$ = new BehaviorSubject<PatchingItemTreeMask>(null);
  private readonly _patchingItemTreeForm$ = new BehaviorSubject<FormGroup>(null);

  readonly replacingItemTree$ = this._replacingItemTree$.asObservable();
  readonly patchingItemTreeFilter$ = this._patchingItemTreeFilter$.asObservable();
  readonly patchingItemTreeForm$ = this._patchingItemTreeForm$.asObservable();

  constructor(private fb: FormBuilder, private itemTreeStore: EditorBookingItemTreeStore) {
    combineLatest(
      this.itemTreeStore.editingItemTree$,
      this.patchingItemTreeFilter$
    )
    .pipe(
      filter(
        ([editingItemTree, _patchingItemTreeFilter]) =>
          editingItemTree !== null
      )
    )
    .subscribe(
      ([editingItemTree, patchingItemTreeFilter]) => {
        this.initialize(
          editingItemTree,
          patchingItemTreeFilter
        );
      }
    );

    this.itemTreeStore.editingItemTree$
    .pipe(
      map(
        (editingItemTree) =>
          editingItemTree !== null
      ),
      distinctUntilChanged(),
      filter(
        isEditing => !isEditing
      )
    )
    .subscribe(
      _ => this.terminate()
    );
  }

  private get replacingItemTree(): EditorBookingItemTree {
    return this._replacingItemTree$.getValue();
  }

  private set replacingItemTree(value: EditorBookingItemTree) {
    this._replacingItemTree$.next(value);
  }

  private get patchingItemTreeFilter(): PatchingEditorBookingItemTreeFilter {
    return this._patchingItemTreeFilter$.getValue();
  }

  private set patchingItemTreeFilter(value: PatchingEditorBookingItemTreeFilter) {
    this._patchingItemTreeFilter$.next(value);
  }

  private get patchingItemTreeMask(): PatchingItemTreeMask {
    return this._patchingItemTreeMask$.getValue();
  }

  private set patchingItemTreeMask(value: PatchingItemTreeMask) {
    this._patchingItemTreeMask$.next(value);
  }

  private get patchingItemTreeForm(): FormGroup {
    return this._patchingItemTreeForm$.getValue();
  }

  private set patchingItemTreeForm(value: FormGroup) {
    this._patchingItemTreeForm$.next(value);
  }

  initialize(itemTree: EditorBookingItemTree, patchingItemTreeFilter: PatchingEditorBookingItemTreeFilter) {
    this.replacingItemTree = itemTree;

    const patchingItemTreeMask = PatchingItemTreeMask.getFromPatchingItemTreeFilter(patchingItemTreeFilter);

    this.patchingItemTreeMask = patchingItemTreeMask;
    this.patchingItemTreeForm = this.buildItemTreeForm(itemTree, patchingItemTreeMask);
  }

  terminate() {
    this.patchingItemTreeForm = null;
    this.patchingItemTreeMask = null;
    this.replacingItemTree = null;
  }

  startEditing(patchingItemTreeFilter: PatchingEditorBookingItemTreeFilter) {
    this.patchingItemTreeFilter = patchingItemTreeFilter;
    this.itemTreeStore.startEditingItemTree();
  }

  stopEditing() {
    this.itemTreeStore.stopEditingItemTree();
    this.patchingItemTreeFilter = null;
  }

  save() {
    const patchingItemTreeForm = this.patchingItemTreeForm;
    const patchingItemTreeMask = this.patchingItemTreeMask;

    const replacingItemTree = this.replacingItemTree.createCopyWithoutItemList();

    if (patchingItemTreeMask.bookingType) {
      replacingItemTree.bookingType = patchingItemTreeForm.value.bookingType;
    }

    if (patchingItemTreeMask.examType) {
      replacingItemTree.examType = patchingItemTreeForm.value.examType;
      replacingItemTree.risrAssessConnectionId = patchingItemTreeForm.value.risrAssessConnectionId;
      replacingItemTree.risrAssessBlueprintId = patchingItemTreeForm.value.risrAssessBlueprintId;
      replacingItemTree.risrAssessItemSetGenerationMethod = patchingItemTreeForm.value.risrAssessItemSetGenerationMethod;
      replacingItemTree.risrAssessItemSetNumber = patchingItemTreeForm.value.risrAssessItemSetNumber;
    }

    if (patchingItemTreeMask.extraRoles) {
      replacingItemTree.extraRoles = patchingItemTreeForm.value.extraRoles;
    }

    this.itemTreeStore.replaceItemTree(replacingItemTree);
  }

  private buildItemTreeForm(itemTree: EditorBookingItemTree, patchingItemTreeMask: PatchingItemTreeMask): FormGroup {
    const controlsConfig: any = {};

    if (patchingItemTreeMask.bookingType) {
      controlsConfig.bookingType = this.fb.control(itemTree.bookingType);
    }

    if (patchingItemTreeMask.examType) {
      controlsConfig.examType = this.fb.control(itemTree.examType);
      controlsConfig.risrAssessConnectionId = this.fb.control(itemTree.risrAssessConnectionId);
      controlsConfig.risrAssessBlueprintId = this.fb.control(itemTree.risrAssessBlueprintId);
      controlsConfig.risrAssessItemSetGenerationMethod = this.fb.control(itemTree.risrAssessItemSetGenerationMethod);
      controlsConfig.risrAssessItemSetNumber = this.fb.control(itemTree.risrAssessItemSetNumber);
    }

    if (patchingItemTreeMask.extraRoles) {
      controlsConfig.extraRoles = this.fb.control(itemTree.extraRoles);
    }

    return this.fb.group(controlsConfig);
  }
}
